/**
 * Connections — Integration Hub (Fase 1)
 * -----------------------------------------------------------------------------
 * Página de gestão das conexões com sistemas de terceiros (ERPs, rastreadores,
 * plataformas de associação). É a porta de entrada do módulo Inteligência de
 * Dados: sem conexão configurada não há importação nem consulta ao vivo.
 *
 * Toda leitura é feita por RPC SECURITY DEFINER e todo teste de conexão passa
 * pela edge function `integration-gateway` (nenhuma credencial no navegador).
 *
 * @version 1.2.0
 * RELEASE: 2026-08-24 v1.2.0 - Clicar na conexão abre o modal de aplicativos já no
 *          conector; editar abre o modal direto no formulário. Status passa a ser
 *          bolinha + texto (ConnectionStatusDot) no lugar do badge colorido.
 * RELEASE: 2026-08-23 v1.1.0 - "Nova conexão" abre o catálogo de aplicativos e a
 *          edição leva à página do fornecedor. Exibe a conexão padrão.
 * RELEASE: 2026-08-23 v1.0.0 - Criação inicial (Integration Hub Fase 1).
 */

import { useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Pencil,
  Plug,
  Plus,
  Search,
  Star,
  Trash2,
  Zap,
} from 'lucide-react';

import {
  ListPageLayout,
  ListPageHeader,
  ListPageKpis,
  ListPageFiltersBar,
  ListPageContent,
  KpiCard,
} from '@/components/layout/list-page';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { IntegrationCatalogDialog } from '@/components/data/IntegrationCatalogDialog';
import { ConnectionStatusDot } from '@/components/data/ConnectionStatusDot';
import {
  useIntegrationConnections,
  type IntegrationConnection,
} from '@/hooks/integrations/useIntegrationConnections';

/** Página "Conexões" do módulo Inteligência de Dados */
export default function Connections() {
  const [search, setSearch] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [catalogOpen, setCatalogOpen] = useState(false);
  /** Conector/conexão que o modal deve abrir diretamente (usabilidade) */
  const [catalogTarget, setCatalogTarget] = useState<{
    slug: string | null;
    editConnectionId: string | null;
  }>({ slug: null, editConnectionId: null });
  const [deleting, setDeleting] = useState<IntegrationConnection | null>(null);

  const {
    providers,
    isLoadingProviders,
    connections,
    isLoading,
    deleteConnection,
    testConnection,
    setDefaultConnection,
  } = useIntegrationConnections({
    search,
    providerSlug: providerFilter === 'all' ? null : providerFilter,
  });

  /** Lista completa (sem filtro) usada pelos contadores do catálogo */
  const { connections: allConnections } = useIntegrationConnections();

  /** KPIs calculados no cliente (lista pequena por natureza) */
  const kpis = useMemo(() => {
    const total = connections.length;
    // Conectadas = status 'active' (gravado pelo teste) ou legado 'connected'
    const connected = connections.filter((c) => c.status === 'active' || c.status === 'connected').length;
    const withError = connections.filter((c) => c.status === 'error').length;
    return { total, connected, withError };
  }, [connections]);

  /** Abre o catálogo de aplicativos (fluxo de criação) */
  const handleCreate = () => {
    setCatalogTarget({ slug: null, editConnectionId: null });
    setCatalogOpen(true);
  };

  /** Clique na linha: abre o modal já dentro do conector da conexão */
  const handleOpenConnection = (connection: IntegrationConnection) => {
    setCatalogTarget({ slug: connection.provider_slug, editConnectionId: null });
    setCatalogOpen(true);
  };

  /** Edição: abre o modal já no formulário daquela conexão */
  const handleEdit = (connection: IntegrationConnection) => {
    setCatalogTarget({ slug: connection.provider_slug, editConnectionId: connection.id });
    setCatalogOpen(true);
  };


  return (
    <ListPageLayout>
      <ListPageHeader
        title="Conexões"
        actions={
          <Button size="xs" onClick={handleCreate} className="gap-1.5">
            <Plus />
            Nova conexão
          </Button>
        }
      />

      <ListPageKpis className="border-b-0 pb-1">
        <KpiCard color="blue" icon={Plug} label="Conexões" value={kpis.total} />
        <KpiCard color="green" icon={CheckCircle2} label="Conectadas" value={kpis.connected} />
        <KpiCard color="red" icon={AlertTriangle} label="Com erro" value={kpis.withError} />
      </ListPageKpis>

      <ListPageFiltersBar className="border-b-0 bg-transparent">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            maxLength={100}
            placeholder="Buscar conexão..."
            className="pl-8"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select value={providerFilter} onValueChange={setProviderFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Sistema" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os sistemas</SelectItem>
            {providers.map((p) => (
              <SelectItem key={p.slug} value={p.slug}>
                {p.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </ListPageFiltersBar>

      <ListPageContent>
        {isLoading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : connections.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <Plug className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhuma conexão configurada</p>
            <p className="text-xs text-muted-foreground">
              Conecte um ERP, rastreador ou plataforma para começar a importar dados.
            </p>
            <Button variant="outline" onClick={handleCreate} className="mt-2 gap-1.5">
              <Plus className="h-4 w-4" />
              Nova conexão
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {connections.map((connection) => {
              const isTesting =
                testConnection.isPending && testConnection.variables === connection.id;

              return (
                <div
                  key={connection.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenConnection(connection)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleOpenConnection(connection);
                    }
                  }}
                  className="flex cursor-pointer flex-col gap-3 rounded-lg border p-4 transition-colors hover:border-foreground/30 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  {/* Identificação */}
                  <div className="min-w-0 space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-sm font-medium">{connection.name}</span>
                      <ConnectionStatusDot status={connection.status} />
                      {connection.is_default && (
                        <Badge variant="outline" className="gap-1">
                          <Star className="h-3 w-3" />
                          Padrão
                        </Badge>
                      )}
                      {!connection.is_active && <Badge variant="outline">Inativa</Badge>}
                    </div>

                    <p className="truncate text-xs text-muted-foreground">
                      {connection.provider_name}
                      {connection.base_url ? ` · ${connection.base_url}` : ''}
                    </p>
                    {connection.status === 'error' && connection.last_error && (
                      <p className="truncate text-xs text-destructive">{connection.last_error}</p>
                    )}
                  </div>

                  {/* Ações (não devem abrir o modal da linha) */}
                  <div
                    className="flex shrink-0 items-center gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {!connection.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        disabled={setDefaultConnection.isPending}
                        onClick={() => setDefaultConnection.mutate(connection.id)}
                      >
                        <Star className="h-3.5 w-3.5" />
                        Definir padrão
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5"
                      disabled={isTesting}
                      onClick={() => testConnection.mutate(connection.id)}
                    >
                      {isTesting ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Zap className="h-3.5 w-3.5" />
                      )}
                      Testar
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(connection)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive"
                      onClick={() => setDeleting(connection)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ListPageContent>

      {/* Catálogo de aplicativos: ponto de entrada único para novas conexões */}
      <IntegrationCatalogDialog
        open={catalogOpen}
        onOpenChange={setCatalogOpen}
        initialProviderSlug={catalogTarget.slug}
        initialEditConnectionId={catalogTarget.editConnectionId}
        providers={providers}
        isLoading={isLoadingProviders}
        connections={allConnections}
      />


      {/* Confirmação de exclusão */}
      <AlertDialog open={Boolean(deleting)} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir conexão</AlertDialogTitle>
            <AlertDialogDescription>
              A conexão "{deleting?.name}" e suas credenciais serão removidas. Importações e
              consultas que dependem dela deixarão de funcionar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleting) deleteConnection.mutate(deleting.id);
                setDeleting(null);
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ListPageLayout>
  );
}
